import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { colors, formatNumber } from '../styles/tokens'
import { Button, Input, Select } from './FormControls'
import { calculateExcavation } from '../lib/quantityEngine'
import { useDraftPersistence } from '../lib/useDraftPersistence'

// =======================================================================
// ExcavationModulePanel — Excavation and Backfilling share this ONE
// panel and the ONE calculateExcavation() engine. Backfilling depends
// on OTHER already-Saved, Active Resource Quantity Outputs from the
// SAME Scope (Excavation source + selected Concrete Volume deductions)
// — this panel is responsible only for selecting those dependencies,
// retrieving their authoritative saved values, and passing plain
// numbers into the existing engine. No second calculation engine.
// =======================================================================

// Builds a human-readable label from ONLY data that is guaranteed to
// exist on every resource_quantity_output row — module_key,
// calculated_value, unit. No field like "Footing F1" is fabricated;
// engineering_inputs content varies per module and cannot be relied on
// as an authoritative descriptive name across all module types.
const MODULE_DISPLAY_NAMES = { excavation: 'Excavation', concrete_volume: 'Concrete Volume' }
function buildOutputLabel(output) {
  if (!output) return ''
  const name = MODULE_DISPLAY_NAMES[output.module_key] || output.module_key
  return `${name} — ${Number(output.calculated_value).toFixed(2)} ${output.unit}`
}

export default function ExcavationModulePanel({ scopeId, boqItemId, superseding, onClose, onError, saving, onSave }) {
  const draftKey = `draft:excavation:${scopeId || boqItemId || 'unknown'}`
  const [draft, setDraft, clearDraft] = useDraftPersistence(draftKey, {
    mode: 'excavation', area: '', depth: '', excavationSourceOutputId: null, selectedPermanentVolumeOutputIds: [],
  })
  const initial = superseding ? {
    mode: superseding.engineering_inputs?.mode || 'excavation',
    area: superseding.engineering_inputs?.area ?? '', depth: superseding.engineering_inputs?.depth ?? '',
    excavationSourceOutputId: superseding.engineering_inputs?.excavationSourceOutputId || null,
    selectedPermanentVolumeOutputIds: superseding.engineering_inputs?.selectedPermanentVolumeOutputIds || [],
  } : draft

  const [mode, setModeRaw] = useState(initial.mode)
  const [area, setAreaRaw] = useState(initial.area)
  const [depth, setDepthRaw] = useState(initial.depth)
  const [excavationSourceOutputId, setExcavationSourceOutputIdRaw] = useState(initial.excavationSourceOutputId)
  const [selectedPermanentVolumeOutputIds, setSelectedPermanentVolumeOutputIdsRaw] = useState(new Set(initial.selectedPermanentVolumeOutputIds))
  const [availableOutputs, setAvailableOutputs] = useState({ excavation: [], concrete: [] })
  const [dependencyChanged, setDependencyChanged] = useState(false)

  const mk = (setter, key) => (v) => { setter(v); if (!superseding) setDraft((d) => ({ ...d, [key]: v instanceof Set ? [...v] : v })) }
  const setMode = mk(setModeRaw, 'mode')
  const setArea = mk(setAreaRaw, 'area')
  const setDepth = mk(setDepthRaw, 'depth')
  const setExcavationSourceOutputId = mk(setExcavationSourceOutputIdRaw, 'excavationSourceOutputId')
  const setSelectedPermanentVolumeOutputIds = mk(setSelectedPermanentVolumeOutputIdsRaw, 'selectedPermanentVolumeOutputIds')

  // Dependency candidates: same Scope, Active only, correct module_key.
  // Self-reference is prevented structurally below (excavation list and
  // concrete list are mutually exclusive module_keys, and the concrete
  // checkboxes never include the chosen excavation source).
  useEffect(() => {
    if (mode !== 'backfilling' || !scopeId) return
    ;(async () => {
      const { data, error } = await supabase.from('resource_quantity_output')
        .select('id, module_key, calculated_value, unit, engineering_inputs')
        .eq('scope_id', scopeId).eq('is_active', true).in('module_key', ['excavation', 'concrete_volume'])
      if (error) { onError(error.message); return }
      setAvailableOutputs({
        excavation: (data || []).filter((o) => o.module_key === 'excavation' && o.engineering_inputs?.mode === 'excavation'),
        concrete: (data || []).filter((o) => o.module_key === 'concrete_volume'),
      })
    })()
  }, [mode, scopeId, onError])

  const excavationSource = availableOutputs.excavation.find((o) => o.id === excavationSourceOutputId)
  const selectedPermanentVolumes = availableOutputs.concrete.filter((o) => selectedPermanentVolumeOutputIds.has(o.id))

  // Dependency Changed detection: compare current live values against
  // the frozen values from when this Backfill Output was last saved —
  // never silently updates the saved historical quantity.
  useEffect(() => {
    if (!superseding || mode !== 'backfilling') { setDependencyChanged(false); return }
    const frozenExc = superseding.engineering_inputs?.frozenExcavationValue
    const frozenPerm = superseding.engineering_inputs?.frozenPermanentVolumesUsed || []
    const liveExc = excavationSource?.calculated_value
    const liveExcChanged = liveExc != null && frozenExc != null && Number(liveExc) !== Number(frozenExc)
    const livePermChanged = frozenPerm.some((f) => {
      const live = availableOutputs.concrete.find((o) => o.id === f.outputId)
      return live && Number(live.calculated_value) !== Number(f.value)
    })
    setDependencyChanged(liveExcChanged || livePermChanged)
  }, [superseding, mode, excavationSource, availableOutputs])

  const result = useMemo(() => {
    try {
      if (mode === 'excavation') {
        return { value: calculateExcavation({ mode: 'excavation', area: area === '' ? NaN : Number(area), depth: depth === '' ? NaN : Number(depth) }), error: null }
      }
      if (!excavationSource) return { value: null, error: 'Select an Excavation source Output.' }
      // REAL logic guard (not just the UI list filter above) — explicitly
      // validates the FINAL state, so this catches the state-transition
      // case where a deduction was selected first and the same output
      // was later assigned as the excavation source, without assuming
      // the old Set is automatically clean.
      if (selectedPermanentVolumeOutputIds.has(excavationSourceOutputId)) {
        return { value: null, error: 'Self-reference: the selected Excavation source cannot also be selected as a permanent volume deduction. Remove it from the deductions list.' }
      }
      return {
        value: calculateExcavation({
          mode: 'backfilling',
          excavationVolume: Number(excavationSource.calculated_value),
          permanentVolumes: selectedPermanentVolumes.map((o) => ({ label: buildOutputLabel(o), value: Number(o.calculated_value) })),
        }),
        error: null,
      }
    } catch (e) { return { value: null, error: e.message } }
  }, [mode, area, depth, excavationSource, excavationSourceOutputId, selectedPermanentVolumeOutputIds, selectedPermanentVolumes])

  async function handleSave() {
    if (!result.value) return
    // Defence-in-depth: re-validate at the save boundary itself, not
    // only relying on `result` having already blocked it above.
    if (mode === 'backfilling' && selectedPermanentVolumeOutputIds.has(excavationSourceOutputId)) {
      onError('Self-reference: the Excavation source cannot also be a selected deduction.')
      return
    }
    const engineeringInputs = mode === 'excavation'
      ? { mode, area: Number(area), depth: Number(depth) }
      : {
          mode, excavationSourceOutputId, excavationSourceLabel: buildOutputLabel(excavationSource),
          selectedPermanentVolumeOutputIds: [...selectedPermanentVolumeOutputIds],
          frozenExcavationValue: Number(excavationSource.calculated_value),
          frozenPermanentVolumesUsed: selectedPermanentVolumes.map((o) => ({ outputId: o.id, label: buildOutputLabel(o), value: Number(o.calculated_value) })),
        }
    const succeeded = await onSave({
      calculatedValueTon: result.value.finalQuantity, unit: 'm3',
      geometrySnapshot: { items: [] }, engineeringInputs,
      breakdownSnapshot: { mode, ...result.value },
      takeoffItemIdForProvenance: null,
    })
    if (succeeded !== false) clearDraft()
  }

  return (
    <div>
      <LabeledSelect label="Mode" value={mode} onChange={(e) => setMode(e.target.value)}>
        <option value="excavation">Excavation</option><option value="backfilling">Backfilling</option>
      </LabeledSelect>

      {mode === 'excavation' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <LabeledInput label="Plan Area (m²)" type="number" value={area} onChange={(e) => setArea(e.target.value)} />
          <LabeledInput label="Depth (m)" type="number" value={depth} onChange={(e) => setDepth(e.target.value)} />
        </div>
      ) : (
        <>
          {dependencyChanged && (
            <div style={{ padding: '8px 10px', background: '#FFF8E1', border: '1px solid #F5C542', borderRadius: 6, fontSize: 11, color: '#7A5C00', marginBottom: 10 }}>
              ⚠ Dependency Changed — a selected Excavation or Concrete Volume Output has a different value now than when this Backfill was saved. Recalculate to use the current values.
            </div>
          )}
          <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>Excavation Source (Saved, Active, same Scope)</label>
          <Select value={excavationSourceOutputId || ''} onChange={(e) => setExcavationSourceOutputId(e.target.value || null)} style={{ width: '100%', marginBottom: 10 }}>
            <option value="">— Select —</option>
            {availableOutputs.excavation.map((o) => <option key={o.id} value={o.id}>{buildOutputLabel(o)}</option>)}
          </Select>

          <label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>Permanent Volume Deductions (select explicitly — never automatic)</label>
          <div style={{ border: `1px solid ${colors.border}`, borderRadius: 6, maxHeight: 120, overflowY: 'auto', marginBottom: 10 }}>
            {availableOutputs.concrete.filter((o) => o.id !== excavationSourceOutputId).map((o) => (
              <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: `1px solid ${colors.border}`, fontSize: 12 }}>
                <input type="checkbox" checked={selectedPermanentVolumeOutputIds.has(o.id)}
                  onChange={() => setSelectedPermanentVolumeOutputIds((prev) => { const n = new Set(prev); n.has(o.id) ? n.delete(o.id) : n.add(o.id); return n })} />
                {buildOutputLabel(o)}
              </label>
            ))}
          </div>
        </>
      )}

      {result.value && (
        <div style={{ margin: '12px 0', padding: '10px 12px', background: '#F8FAFF', border: `1px solid ${colors.primary}`, borderRadius: 8 }}>
          {result.value.steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
              <span style={{ color: colors.textSecondary }}>{s.label}</span><span style={{ fontWeight: 600 }}>{formatNumber(s.value)} {s.unit}</span>
            </div>
          ))}
        </div>
      )}
      {result.error && <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', marginBottom: 14 }}>{result.error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!superseding && <button onClick={() => { if (window.confirm('Discard draft?')) { clearDraft(); onClose() } }} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Discard Draft</button>}
        <Button onClick={handleSave} disabled={!result.value || saving} style={{ marginLeft: 'auto' }}>{saving ? 'Saving…' : superseding ? 'Save New Version' : 'Save Output'}</Button>
      </div>
    </div>
  )
}
function LabeledInput({ label, ...p }) { return <div style={{marginBottom:6}}><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label><Input {...p} style={{ fontSize: 11, width: '100%' }} /></div> }
function LabeledSelect({ label, children, ...p }) { return <div style={{marginBottom:6}}><label style={{ display: 'block', fontSize: 10, color: colors.textMuted, marginBottom: 2 }}>{label}</label><Select {...p} style={{ fontSize: 11, width: '100%' }}>{children}</Select></div> }
